import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    ShieldCheck,
    Globe2,
    Stethoscope,
    FileText,
    Network,
    Lock,
    AlertTriangle,
} from "lucide-react";

const TABS = [
    {
        id: "overview",
        icon: ShieldCheck,
        label: "Overview",
        accent: "text-cyan-300",
        accentBg: "bg-cyan-300/10 border-cyan-300/25",
    },
    {
        id: "gdpr",
        icon: Globe2,
        label: "GDPR",
        accent: "text-electric",
        accentBg: "bg-electric/10 border-electric/25",
    },
    {
        id: "hipaa",
        icon: Stethoscope,
        label: "HIPAA",
        accent: "text-emerald-300",
        accentBg: "bg-emerald-300/10 border-emerald-300/25",
    },
    {
        id: "sub-processors",
        icon: Network,
        label: "Sub-processors",
        accent: "text-amber-glow",
        accentBg: "bg-amber-300/10 border-amber-300/25",
    },
    {
        id: "dpa",
        icon: FileText,
        label: "DPA",
        accent: "text-violet-300",
        accentBg: "bg-violet-300/10 border-violet-300/25",
    },
    {
        id: "security",
        icon: Lock,
        label: "Security",
        accent: "text-rose-300",
        accentBg: "bg-rose-300/10 border-rose-300/25",
    },
];

const CONTENT = {
    overview: [
        {
            h: "Our compliance posture",
            p: "ApexBrain processes operational data for businesses across 84 countries, including the EU, UK, US and India. Compliance is treated as a product surface — controls are encoded in the platform, not bolted on as policy PDFs.",
        },
        {
            h: "What we are audited against",
            p: "SOC 2 Type II (continuous), ISO/IEC 27001:2022, GDPR (Articles 5, 6, 25, 28, 32, 33, 35, 44), and HIPAA Security & Privacy Rules. Reports are available under NDA from trust@apexbrain.in.",
        },
        {
            h: "Data residency",
            p: "Customers can pin tenancy to EU, US, India or APAC regions. Cross-region transfer requires explicit admin approval and is recorded in the audit log.",
        },
    ],
    gdpr: [
        {
            h: "Roles",
            p: "ApexBrain Operations Pvt. Ltd. acts as the data processor on behalf of the customer (the controller). Where ApexBrain processes personal data of its own users (e.g., billing), it acts as a controller.",
        },
        {
            h: "Lawful basis",
            p: "We rely on (a) contractual necessity for service delivery, (b) legitimate interests for service security and fraud prevention, (c) explicit consent where required (e.g., marketing communications).",
        },
        {
            h: "Data subject rights",
            p: "Customers can export, rectify, restrict or delete any personal data through the in-product admin console. Requests routed via the customer’s controller are completed within 30 days at no extra cost.",
        },
        {
            h: "Transfers outside the EEA",
            p: "Transfers rely on the EU SCCs (2021 modules 2 and 3), supplemented by encryption-in-transit, encryption-at-rest, and access controls bound to least privilege.",
        },
        {
            h: "Breach notification",
            p: "We commit to notifying the customer’s designated security contact within 24 hours of confirmation of a personal data breach involving their tenant.",
        },
    ],
    hipaa: [
        {
            h: "Business Associate Agreement",
            p: "ApexBrain enters into a BAA with covered entities and business associates on request. A standard template is available; redlines reviewed by Legal within 5 business days.",
        },
        {
            h: "ePHI handling",
            p: "When configured for HIPAA workloads, the tenant enables PHI mode: stricter audit logs, mandatory MFA, automatic session timeouts, and disabled features that risk PHI exposure (e.g., public sharing links).",
        },
        {
            h: "Administrative safeguards",
            p: "Role-based access, periodic access reviews, mandatory security awareness training, sanction policy, contingency plans and an incident response runbook tuned for PHI.",
        },
        {
            h: "Technical safeguards",
            p: "AES-256 at rest, TLS 1.3 in transit, key separation per tenant, audit log immutability via append-only storage, and automatic anomaly detection on PHI access patterns.",
        },
        {
            h: "Physical safeguards",
            p: "Infrastructure runs on SOC 2 / ISO 27001-certified cloud providers in regions chosen by the customer. No data is stored on portable media.",
        },
    ],
    "sub-processors": [
        {
            h: "Current list (June 2026)",
            p: "AWS (compute & storage · us-east, eu-west, ap-south), Google Cloud (analytics, AI inference · regional), Stripe (billing), Resend (transactional email), Sentry (error monitoring). All under DPAs with SCCs where applicable.",
        },
        {
            h: "Change management",
            p: "We notify customers of new sub-processors at least 30 days in advance via the trust page and email. Customers may object within 14 days; we will work in good faith to resolve.",
        },
    ],
    dpa: [
        {
            h: "Standard DPA",
            p: "Our Data Processing Addendum incorporates the EU SCCs (2021), UK IDTA addendum, and Indian DPDP Act 2023 schedule. Available at apexbrain.in/dpa for self-signature.",
        },
        {
            h: "What it covers",
            p: "Subject-matter, duration, nature & purpose of processing, types of personal data, categories of data subjects, controller & processor obligations, sub-processing, audit rights, breach handling, deletion or return on termination.",
        },
        {
            h: "How to execute",
            p: "Self-serve via the in-product Legal tab, or email legal@apexbrain.in. Mutual electronic signature is accepted in all jurisdictions where legally valid.",
        },
    ],
    security: [
        {
            h: "Encryption",
            p: "AES-256-GCM at rest, TLS 1.3 in transit, per-tenant data keys wrapped with envelope encryption.",
        },
        // {
        //     h: "Identity & access",
        //     p: "SSO via SAML 2.0 and OIDC, SCIM provisioning, hardware-key MFA support, least-privilege RBAC, just-in-time privileged access with full audit trail.",
        // },
        {
            h: "Operational security",
            p: "Quarterly third-party penetration tests, continuous bug-bounty program, immutable infrastructure-as-code, automated dependency scanning, signed and reproducible builds.",
        },
        {
            h: "Resilience",
            p: "Multi-AZ deployments, point-in-time recovery to 7 days, tested disaster recovery with RPO 5 min and RTO 60 min, region failover supported on enterprise tier.",
        },
    ],
};

export default function CompliancePage() {
    const [activeId, setActiveId] = useState("overview");
    const active = TABS.find((t) => t.id === activeId) || TABS[0];

    return (
        <div className="pt-28 lg:pt-32" data-testid="compliance-page">
            <section className="relative overflow-hidden py-12 lg:py-16">
                <div className="absolute inset-0 radial-glow pointer-events-none" />
                <div className="relative max-w-7xl mx-auto px-6 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
                    <div className="lg:col-span-7">
                        <p className="font-code text-xs uppercase tracking-[0.25em] text-electric font-semibold mb-3">
                            // compliance · trust · data
                        </p>
                        <h1
                            className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-white leading-[1.04]"
                            data-testid="compliance-title"
                        >
                            Built to be trusted.
                            <br />
                            <span className="gradient-text">
                                Documented end-to-end.
                            </span>
                        </h1>
                    </div>
                    <p className="lg:col-span-5 text-base sm:text-lg text-zinc-400 leading-relaxed font-light">
                        ApexBrain is engineered for regulated industries. SOC
                        2, ISO 27001, GDPR and HIPAA — encoded as controls in
                        the product, not just on this page.
                    </p>
                </div>

                <div className="relative max-w-7xl mx-auto px-6 sm:px-8 mt-10">
                    <div
                        className="rounded-xl border border-amber-300/20 bg-amber-300/[0.05] p-4 flex items-start gap-3"
                        data-testid="compliance-disclaimer"
                    >
                        <AlertTriangle className="shrink-0 w-4 h-4 mt-0.5 text-amber-glow" />
                        <p className="text-[12.5px] text-amber-100/80 leading-relaxed">
                            <span className="font-semibold">Template
                                notice.</span>{" "}
                            The text below is provided as an editable
                            starting point. Have your legal counsel review
                            and customise before publishing.
                        </p>
                    </div>
                </div>
            </section>

            <section className="relative pb-28">
                <div className="max-w-7xl mx-auto px-6 sm:px-8">
                    {/* Sticky tabs */}
                    <div
                        className="sticky top-24 z-20 -mx-2 mb-8 px-2 py-3 bg-obsidian-300/90 backdrop-blur border-y border-white/[0.06]"
                        data-testid="compliance-tabs"
                    >
                        <div className="flex flex-wrap gap-2">
                            {TABS.map((t) => {
                                const isActive = activeId === t.id;
                                return (
                                    <button
                                        key={t.id}
                                        onClick={() => setActiveId(t.id)}
                                        data-testid={`compliance-tab-${t.id}`}
                                        className={`inline-flex items-center gap-2 rounded-full border px-3.5 py-1.5 text-xs font-semibold transition ${
                                            isActive
                                                ? `${t.accentBg} text-white`
                                                : "bg-white/[0.02] border-white/[0.08] text-zinc-400 hover:bg-white/[0.05]"
                                        }`}
                                    >
                                        <t.icon
                                            className={`w-3.5 h-3.5 ${isActive ? t.accent : "text-zinc-500"}`}
                                        />
                                        <span>{t.label}</span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <AnimatePresence mode="wait">
                        <motion.div
                            key={active.id}
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0, transition: { duration: 0.3, ease: [0.16, 1, 0.3, 1] } }}
                            exit={{ opacity: 0, y: -6, transition: { duration: 0.15, ease: [0.4, 0, 1, 1] } }}
                            className="grid grid-cols-1 lg:grid-cols-12 gap-6"
                            data-testid={`compliance-content-${active.id}`}
                        >
                            <aside className="lg:col-span-3">
                                <div className="rounded-xl border border-white/[0.08] bg-obsidian-200/70 p-5 sticky top-44">
                                    <span
                                        className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 ${active.accentBg} mb-3`}
                                    >
                                        <active.icon
                                            className={`w-3.5 h-3.5 ${active.accent}`}
                                        />
                                        <span
                                            className={`text-[10px] font-code uppercase tracking-[0.2em] font-semibold ${active.accent}`}
                                        >
                                            {active.label}
                                        </span>
                                    </span>
                                    <p className="text-xs text-zinc-500 leading-relaxed">
                                        Questions on {active.label}? Email{" "}
                                        <a
                                            className="text-white hover:underline"
                                            href="mailto:trust@apexbrain.in"
                                        >
                                            trust@apexbrain.in
                                        </a>{" "}
                                        — typical response within 1 business
                                        day under NDA.
                                    </p>
                                </div>
                            </aside>

                            <article className="lg:col-span-9 rounded-2xl border border-white/[0.08] bg-obsidian-200/70 backdrop-blur-sm p-7 sm:p-10 space-y-8">
                                {CONTENT[active.id].map((s, i) => (
                                    <section
                                        key={s.h}
                                        data-testid={`compliance-section-${active.id}-${i}`}
                                    >
                                        <h3 className="font-outfit text-lg sm:text-xl font-semibold text-white">
                                            {String(i + 1).padStart(2, "0")} ·{" "}
                                            {s.h}
                                        </h3>
                                        <p className="mt-3 text-sm sm:text-base text-zinc-400 leading-relaxed font-light">
                                            {s.p}
                                        </p>
                                    </section>
                                ))}
                                <div className="pt-4 border-t border-white/[0.06] flex flex-wrap items-center justify-between gap-3 text-[11px] font-code text-zinc-500">
                                    <span>
                                        last updated · 14 June 2026 · v1.4
                                    </span>
                                    <a
                                        href="mailto:trust@apexbrain.in"
                                        className="text-zinc-300 hover:text-white transition"
                                    >
                                        request the signed PDF →
                                    </a>
                                </div>
                            </article>
                        </motion.div>
                    </AnimatePresence>
                </div>
            </section>
        </div>
    );
}
